// 第三方库导入，按字母顺序排列
import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
// import { useParams } from '@tanstack/react-router'
import {
  Button,
  Form,
  Image,
  Input,
  Radio,
  Result,
  Selector,
  Space,
  Steps,
  Toast,
} from 'antd-mobile'
// 本地模块导入，按字母顺序排列
import Body from '@/components/Body'
import {
  AccommodationType,
  EnrollStep,
  OrderStatusToEnrollStep,
  STEPS_CONFIG,
  type Order,
} from '@/types/info'
import { useOrder } from '@/hook/useOrderList'
import SpinPending from '@/components/SpinPending'
import AgreementService from '@/service/agreementService'
import { useAccommodationFlow } from '@/hook/useAccommodation'
import { useWeiPayFlow } from '@/hook/useWeiPayFlow'
import { useRegion } from '@/hook/useRegionList'
import useRefresh from '@/hook/useRefresh'
import { useOrderPageLifecycle } from '@/hook/useOrderPageLifecycle'
// import { decodeOrderId } from '@/utils/orderIdEncoder'
import { useEnsureEncodedOrderId } from '@/hook/useEnsureEncodedOrderId'
import { OrderToast } from '@/utils/toastBus'
import dayjs from 'dayjs'
import { useOrderCountdownStore } from '@/store/orderCountdown'

// 定义订单状态步骤映射
const ORDER_STEP: Record<EnrollStep, number> = {
  [EnrollStep.inputtting]: 0,
  [EnrollStep.canceled]: 0,
  [EnrollStep.paying]: 1,
  [EnrollStep.selecting]: 2,
  [EnrollStep.allocating]: 3,
  [EnrollStep.end]: 3,
  [EnrollStep.refunded]: 3,
}


// 定义订单状态按钮类型
export type NextStep = {
  btn?: React.ReactNode
  nextEnrollStep: Exclude<EnrollStep, EnrollStep.inputtting>
}
// 轮询逻辑已迁移至 useOrderPageLifecycle
const agreementList = AgreementService.data
// 定义 RouteComponent 组件
const RouteComponent: React.FC = () => {
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)
  //#region 订单相关
  const { orderId, encodedOrRaw: encodedOrderId } = useEnsureEncodedOrderId()
  const {
    data: order,
    isLoading: orderIsLoading,
    refetch: refetchOrder,
    error:orderError,
    isFetching:orderIsFetching
  } = useOrder(orderId)
  // 已迁移到 useOrderPageLifecycle 内部维护
  useRefresh([refetchOrder])
  // 页面生命周期封装（轮询 + 加载态管理 + Toast 清理）
  const enrollStep = order ? OrderStatusToEnrollStep[order.order_status] : null
  useOrderPageLifecycle({
    enrollStep,
    refetchOrder,
    setIsLoading,
    order,
  })
  //#endregion

  const { startPay } = useWeiPayFlow({
    orderId,
    enrollStep,
    refetchOrder,
    setIsLoading,
  })

  //#endregion


  //#region 选择宿舍相关
  const {
    data: region,
    isLoading: regionIsLoading,
    isError: regionIsError,
  } = useRegion(order?.region_id)
  useAccommodationFlow({
    orderId,
    order,
    region,
    enrollStep,
    refetchOrder,
    orderIsFetching,
  })
  //#endregion

  const orderIdKey = Number.isFinite(orderId) ? String(orderId) : ''
  const upsertCountdown = useOrderCountdownStore((state) => state.upsertCountdown)
  const removeCountdown = useOrderCountdownStore((state) => state.removeCountdown)
  const countdown = useOrderCountdownStore((state) =>
    orderIdKey ? state.countdowns[orderIdKey] : undefined,
  )
  const remainingSeconds =
    typeof countdown?.remainingSeconds === 'number' ? countdown.remainingSeconds : null

  useEffect(() => {
    if (!orderIdKey) return

    if (enrollStep !== EnrollStep.paying || !order?.expirationTime) {
      removeCountdown(orderIdKey)
      return
    }
    const expireAtMs = dayjs(order.expirationTime).valueOf()
    if (Number.isNaN(expireAtMs)) {
      removeCountdown(orderIdKey)
      return
    }
    upsertCountdown({
      orderId: orderIdKey,
      expireAtMs,
      meta: {
        courseName: order.courseName,
        encodedOrderId,
      },
    })
  }, [
    order?.expirationTime,
    enrollStep,
    orderIdKey,
    upsertCountdown,
    removeCountdown,
    order?.courseName,
    encodedOrderId,
  ])

  const formatSeconds = (total: number) => {
    const mm = String(Math.floor(total / 60)).padStart(2, '0')
    const ss = String(total % 60).padStart(2, '0')
    return `${mm}:${ss}`
  }

  // 倒计时归零时：提示并刷新页面
  useEffect(() => {
    if (enrollStep !== EnrollStep.paying) return
    if (typeof remainingSeconds !== 'number') return
    if (remainingSeconds > 0) return
    const close = OrderToast.orderCancelled(() => {
      window.location.reload()
    })
    return () => close?.()
  }, [remainingSeconds, enrollStep])

  if (orderIsLoading || regionIsLoading) {
    return <SpinPending />
  }
  if (regionIsError) {
    Toast.show('获取区域信息失败，请稍后重试')
  }

  // 处理无效的订单ID格式
  if (isNaN(orderId)) {
    return (
      <Body>
        <Result
          status="error"
          title="无效的订单链接"
          description="请检查链接是否正确"
        />
      </Body>
    )
  }
  if(orderError){
    return (
      <Body>
        <Result
          status="error"
          title={orderError.message}
          description="请检查链接或登录的微信是否正确"
        />
      </Body>
    )
  }
  if (!order) {
    return null
  }
  if (!enrollStep) return null
  const btnText = (() => {
    switch (enrollStep) {
      case EnrollStep.paying:
        return '支付费用'
      case EnrollStep.canceled:
        return '已取消'
      default:
        return null
    }
  })()
  async function handleSubmit(order: Order) {
    setIsLoading(true)
    if (!order) return
    switch (enrollStep) {
      case EnrollStep.paying:
        startPay()
        break
      default:
        setIsLoading(false)
        break
    }
  }
  return (
    <Body>
      <div className="bg-white mb-[7px]">
        <Steps current={ORDER_STEP[enrollStep]}>
          {STEPS_CONFIG.map((step, index) => (
            <Steps.Step key={index} title={step.title} />
          ))}
        </Steps>
      </div>
      <Form
        onFinish={() => handleSubmit(order)}
        form={form}
        style={{ marginBottom: '60px' }}
        disabled
      >
        <Form.Item label="课程名称" disabled={false}>
          <Input value={order.courseName} readOnly />
        </Form.Item>
        <Form.Item
          label={
            <span className="text-[#999] font-[400] text-[15px]">订单号</span>
          }
          layout="horizontal"
          disabled={false}
        >
          <Input value={encodedOrderId} readOnly />
        </Form.Item>
        {enrollStep === EnrollStep.paying && typeof remainingSeconds === 'number' && (
          <Form.Item disabled={false} label={<span className="text-[#999] font-[400] text-[15px]">剩余支付时间</span>}>
            <div style={{ color: '#FF5200', fontWeight: 600 }}>{formatSeconds(remainingSeconds)}</div>
          </Form.Item>
        )}
        {enrollStep===EnrollStep.canceled && (
          <Form.Item label="订单状态" disabled={false}>
            <div style={{ color: '#FF5200', fontWeight: 600 }}> 已取消 </div>
          </Form.Item>
        )}
        {[EnrollStep.allocating, EnrollStep.end].includes(enrollStep) && (
          <>
            <Form.Header>群聊二维码<span className="text-[#FF5200] text-[14px] mt-[10px] ml-[10px]" style={{display:enrollStep === EnrollStep.end ? 'inline' : 'none'}}>请立刻扫码进群，否则视为报名失败！</span></Form.Header>
            <div style={{ height: '200px', position: 'relative' }}>
              {enrollStep === EnrollStep.end ? (
                <Image
                  width={140}
                  fit="cover"
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                  src={order.classQrCodeUrl}
                />
              ) : (
                <Result
                  status="waiting"
                  title="等待分配班级"
                  description="请留意服务号和手机短信通知"
                />
              )}
            </div>
          </>
        )}
        <Form.Header>课程信息</Form.Header>
        <Form.Item label="职位代码">
          {order.positionCodeName.split('%').map((item,index) => (
            <div key={item+index}>{item}</div>
          ))}
        </Form.Item>
        <Form.Item label="授课地点" style={{ position: 'relative' }}>
          {order.regionName}
        </Form.Item>
        <Form.Header>个人信息</Form.Header>
        <Form.Item label="姓名" layout="horizontal">
          <Input value={order.user_name} />
        </Form.Item>
        <Form.Item label="性别">
          <Radio.Group value={order.user_gender}>
            <Space style={{ '--gap': '17px' }}>
              <Radio value="男">男</Radio>
              <Radio value="女">女</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="手机号" layout="horizontal">
          <Input value={order.user_phone} />
        </Form.Item>
        <Form.Item label="笔试分数" layout="horizontal">
          <Input value={order.exam_score} />
        </Form.Item>
        <Form.Item label="是否守擂">
          <Radio.Group value={order.is_zhuangyuan.toString()}>
            <Space style={{ '--gap': '17px' }}>
              <Radio value="true" >
                守擂
              </Radio>
              <Radio value="false">
                攻擂
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="协议选择">
          <AgreementSelector order={order}/>
        </Form.Item>
        <Form.Header>宿舍选择</Form.Header>
        <Form.Item label="宿舍类型" layout="horizontal">
          <Input
            readOnly
            value={
              {
                [AccommodationType.single]: '单人间',
                [AccommodationType.double]: '双人间',
              }[order.accommodationType] || '支付费用后选择'
            }
          />
        </Form.Item>
        <div className="p-[12px] bg-white  mt-0 fixed bottom-0 w-[100%]">
          <Button
            block
            type="submit"
            color="primary"
            size="large"
            loading={isLoading}
            disabled={enrollStep === EnrollStep.canceled}
            style={{
              display: ![EnrollStep.paying, EnrollStep.canceled].includes(
                enrollStep,
              )
                ? 'none'
                : 'block',
            }}
          >
            {btnText}
          </Button>
        </div>
      </Form>
    </Body>
  )
}

export const Route = createFileRoute('/order/$orderId')({
  component: RouteComponent,
})

function AgreementSelector({ order }: { order: Order }) {
  const AgreementOptions = (function (){
    return agreementList.filter(agreement=>agreement.value===order.agreement_type)
  })()
  return (
    <Selector
      options={AgreementOptions}
      value={[order.agreement_type]}
      columns={2}
      style={{
        '--gap-horizontal': '27px',
        paddingInline: '31px',
      }}
    />
  )
}